import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getImpersonationEmail,
  setImpersonationEmail,
  subscribeImpersonation,
} from './impersonation';

/**
 * React hook for the "view as user" feature.
 *
 * - `targetEmail`: who we're currently viewing as (null when not impersonating)
 * - `setTarget(email)`: switch identities — invalidates every React Query
 *    cache so all in-flight payloads refetch under the new identity
 * - `clear()`: stop impersonating
 */
export function useImpersonation() {
  const targetEmail = useSyncExternalStore(
    subscribeImpersonation,
    getImpersonationEmail,
    () => null,
  );
  const queryClient = useQueryClient();

  const setTarget = (email: string | null) => {
    setImpersonationEmail(email);
    // Every server-derived query is now stale under the new identity.
    queryClient.invalidateQueries();
  };

  return {
    targetEmail,
    isImpersonating: targetEmail !== null,
    setTarget,
    clear: () => setTarget(null),
  };
}
